import { Controller } from "@hotwired/stimulus"
import { useVisibility } from "stimulus-use"
import { showAlert } from "@lib/utils"
import { get, post, destroy } from "@lib/requests"
import appsignal from "@lib/appsignal"

const TIMEOUT_THRESHOLD_MS = 2 * 60 * 1000

// Connects to data-controller="user-session"
export default class extends Controller {

  static targets = ["expiryModal", "ttl", "record"]

  connect() {
    this.debug = false
    this.debug && console.log("Presence controller connected")
    this.ttl = 60
    useVisibility(this)
    this.checkSession() // Get an initial TTL value
  }

  get userId() {
    return document.body.dataset.userId
  }

  get currentUser() {
    return JSON.parse(document.body.dataset.user)
  }
  
  async checkSession(ttl) {
    this.debug && console.log("Checking session, TTL:", ttl)

    if (!ttl) {
      this.debug && console.log("No TTL provided, requesting from /session_check")

      try {
        const response = await get("/session_check", { responseKind: "json" })
        
        // If we get a 401 response we can log the user out
        if (response.status == 401) {
          this.debug && console.log("Session expired, logging out")
          return this.logout()
        }
        
        // If we get another non-200 we can report the error and return
        if (!response.ok) {
          console.error("Error checking session", response)
          showAlert("Erreur lors de la vérification de la session", { type: "error" })
          return this.resetExpiryModal()
        }
          
        // Else we can carry on and get the TTL
        const data = await response.json
        this.debug && console.log("Received TTL:", data.ttl)
        ttl = data.ttl
      } catch (error) {
        console.error("Error checking session", error)
        return // If there's an error, we don't want to continue nor log the user out so we simply return
      }
    }

    // If the user's session has already expired we can logout
    if (ttl <= 0) {
      this.debug && console.log("TTL <= 0, logging out")
      return this.logout()
    }

    // Set the TTL in ms
    const ttlMS = ttl * 1000

    // Clear any existing timeouts or intervals to avoid duplication
    this.debug && console.log("Clearing existing intervals and timeouts")
    clearTimeout(this.sessionTimeout)
    clearInterval(this.sessionPoll)
    clearInterval(this.ttlInterval)
    
    // If the ttl is a long way away, we can set a timeout to check again x amount of time before it expires
    if (ttlMS > TIMEOUT_THRESHOLD_MS) {
      this.debug && console.log(`TTL > ${TIMEOUT_THRESHOLD_MS / 1000} seconds, setting timeout to recheck session`)
      clearInterval(this.ttlInterval)
      return this.sessionTimeout = setTimeout(this.checkSession.bind(this), ttlMS - TIMEOUT_THRESHOLD_MS)
    } 

    // If the TTL is less than 60 seconds, show the session expiry modal and start an interval countdown
    if (ttl <= 60) {
      this.debug && console.log("TTL <= 60 seconds, showing expiry modal and starting countdown")
      this.ttl = ttl // Set the controller's TTL value
      this.setTTLText(ttl) // Set the initial text for the countdown
      this.expiryModalTarget.classList.remove("hidden") // Show the modal
      this.ttlInterval = setInterval(this.updateTTLCoundown.bind(this), 1000) // Set a countdown interval
    }
    
    // Else if the TTL is within the threshold, we can set an interval to check the session more regularly
    else {
      this.debug && console.log("TTL is within threshold, setting poll interval every 10s")
      this.sessionPoll = setInterval(this.checkSession.bind(this), 10000)
    }
  }

  updateTTLCoundown() {
    this.debug && console.log("Updating TTL countdown, current TTL:", this.ttl)
    
    // If the countdown reaches 0, we can hide the modal and logout the user
    if (this.ttl <= 0) {
      this.debug && console.log("TTL countdown reached 0, logging out")
      this.expiryModalTarget.classList.add("hidden")
      clearInterval(this.ttlInterval)
      return this.logout()
    }
    this.ttl -= 1
    this.setTTLText(this.ttl)
  }

  setTTLText(ttl) {
    this.debug && console.log("Setting TTL text:", ttl)
    if (!this.ttlTarget) return
    this.ttlTarget.textContent = ttl == 1 ? "1 seconde." : `${ttl} secondes.`
  }

  visible() {
    this.debug && console.log("User is visible")
    this.checkSession()
  }

  logLogout() {
    try {
      const span = appsignal.createSpan()
      span.setAction("session-logout")
      span.setNamespace("requests")
      span.setTags({
        user: this.currentUser,
        location_path: window.location.pathname,
      })
    } catch (e) {
      // Never break the logout flow because of logging
      console.error("Error logging session logout", e)
    }
  }

  async logout() {
    this.debug && console.log("Logging out user")

    // Record that an automatic logout was triggered from the frontend
    this.logLogout()

    // Clear any intervals
    clearInterval(this.sessionPoll)
    clearInterval(this.ttlInterval)
    clearTimeout(this.sessionTimeout)
    
    // Hide the modal if it's still visible
    this.expiryModalTarget.classList.add("hidden")
    
    // Find the form with the action users/sign_out and submit it
    const signOutForm = document.querySelector("form[action='/users/sign_out']")

    // Logout
    signOutForm?.submit()
  }

  async extendSession() {
    this.debug && console.log("Extending session")
    try {
      const response = await post("/user_sessions/extend")
      const { ttl } = await response.json
      this.debug && console.log(`Session successfully extended, new TTL: ${ttl}`)
      showAlert("Session prolongée avec succès", { type: "success"} )
      this.resetExpiryModal()
      this.checkSession(ttl)
      
    } catch (error) {
      console.error("Error extending session", error)
    }
  }

  resetExpiryModal() {
    this.debug && console.log("Resetting expiry modal")
    this.expiryModalTarget.classList.add("hidden")
    this.ttl = 60
    clearInterval(this.ttlInterval)
    clearInterval(this.sessionPoll)
    clearTimeout(this.sessionTimeout)
  }

  disconnect() {
    this.debug && console.log("Presence controller disconnected")
    clearInterval(this.ttlInterval)
    clearInterval(this.sessionPoll)
    clearTimeout(this.sessionTimeout)
  }

}
